import {
  CUSTOMER_MOVEMENT_LABELS,
  FISCAL_KIND_LABELS,
  formatCuit,
  ISSUER_CONDITION_LABELS,
  PAYMENT_METHOD_LABELS,
  RECEPTOR_CONDITION_LABELS,
  VAT_RATE_LABELS,
  type CustomerDTO,
  type FiscalDocumentDTO,
  type CustomerMovementDTO,
  type SaleDTO,
  type TicketConfig,
} from '@bifeai/shared';

const money = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 0,
});
const kilos = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});
const when = new Intl.DateTimeFormat('es-AR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const m = (cents: number) => (cents % 100 === 0 ? money : money2).format(cents / 100);
const lines = (text: string) =>
  text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => `<div>${esc(l)}</div>`)
    .join('');

/** "Crédito 3 cuotas", "Débito · cupón 1234". */
const payLabel = (p: SaleDTO['payments'][number]) =>
  esc(
    `${PAYMENT_METHOD_LABELS[p.method]}${p.method === 'CREDIT' && p.installments && p.installments > 1 ? ` ${p.installments} cuotas` : ''}${
      p.reference ? ` · ${p.method === 'DEBIT' || p.method === 'CREDIT' ? 'cupón ' : ''}${p.reference}` : ''
    }`,
  );

const DOC_LABEL: Record<number, string> = { 80: 'CUIT', 86: 'CUIL', 96: 'DNI' };
const money2 = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 });
const m2 = (cents: number) => money2.format(cents / 100);
const ymd = (d: string) => d.split('-').reverse().join('/');

/** La factura (o nota) autorizada de la venta, si tiene. */
export function saleInvoice(sale: SaleDTO): FiscalDocumentDTO | null {
  return sale.fiscal.find((d) => d.kind === 'INVOICE' && d.status === 'AUTHORIZED') ?? null;
}

/**
 * Comprobante electrónico (factura, nota de crédito o débito) en la impresora de tickets:
 * datos del emisor y del cliente, letra y código, IVA, CAE con su vencimiento y el QR de ARCA.
 */
export function fiscalHtml(sale: SaleDTO, doc: FiscalDocumentDTO, cfg: TicketConfig = sale.print.ticket, isCopy = false): string {
  const i = doc.issuer;
  const assoc = doc.associatedId ? sale.fiscal.find((d) => d.id === doc.associatedId) : null;
  const items =
    doc.kind === 'INVOICE'
      ? sale.lines
          .map(
            (l) => `
      <div class="item">${esc(l.productName)}</div>
      <div class="row small"><span>${
        l.unitType === 'WEIGHT' ? `${kilos.format(l.qty / 1000)} kg x ${m(l.unitPriceCents)}/kg` : `${l.qty} u. x ${m(l.unitPriceCents)}`
      }</span><span>${m(l.lineTotalCents)}</span></div>`,
          )
          .join('') + (sale.discountCents > 0 ? `<div class="row"><span>Descuento</span><span>-${m(sale.discountCents)}</span></div>` : '')
      : `<div class="item">${esc(doc.reason ?? FISCAL_KIND_LABELS[doc.kind])}</div>`;
  const vat =
    doc.letter === 'A'
      ? `<div class="row"><span>Neto gravado</span><span>${m2(doc.netCents)}</span></div>${doc.vatBreakdown
          .map((v) => `<div class="row"><span>IVA ${VAT_RATE_LABELS[v.rate]}</span><span>${m2(v.amountCents)}</span></div>`)
          .join('')}${doc.exemptCents ? `<div class="row"><span>Exento</span><span>${m2(doc.exemptCents)}</span></div>` : ''}${
          doc.untaxedCents ? `<div class="row"><span>No gravado</span><span>${m2(doc.untaxedCents)}</span></div>` : ''
        }`
      : '';
  // Ley 27.743 (transparencia fiscal): en la factura B a consumidor final se informa el IVA contenido.
  const transparency =
    doc.letter === 'B' && doc.vatCents > 0
      ? `<div class="sep"></div><div class="small">Régimen de Transparencia Fiscal al Consumidor (Ley 27.743)</div><div class="row small"><span>IVA contenido</span><span>${m2(doc.vatCents)}</span></div>`
      : '';
  return `
    <header>
      <div class="biz">${esc(i.legalName)}</div>
      ${sale.print.businessName && sale.print.businessName !== i.legalName ? `<div>${esc(sale.print.businessName)}</div>` : ''}
      <div>CUIT ${esc(formatCuit(i.cuit))}</div>
      <div>${esc(ISSUER_CONDITION_LABELS[i.ivaCondition])}</div>
      ${i.grossIncomeId ? `<div>IIBB: ${esc(i.grossIncomeId)}</div>` : ''}
      ${i.activityStart ? `<div>Inicio de actividades: ${esc(ymd(i.activityStart))}</div>` : ''}
      ${i.address ? `<div>${esc(i.address)}</div>` : ''}
    </header>
    <div class="sep"></div>
    <div class="fiscal-head"><span class="letter">${doc.letter}</span><div><strong>${esc(FISCAL_KIND_LABELS[doc.kind].toUpperCase())}</strong><div class="small">Cód. ${String(doc.cbteTipo).padStart(3, '0')}</div></div></div>
    <div class="row"><span>Nº ${esc(doc.displayNumber ?? '')}</span><span>${esc(ymd(doc.issueDate))}</span></div>
    ${assoc?.displayNumber ? `<div class="small">Asociada a ${esc(FISCAL_KIND_LABELS[assoc.kind])} ${assoc.letter} ${esc(assoc.displayNumber)}</div>` : ''}
    ${doc.environment === 'HOMO' ? '<div class="void">PRUEBA · SIN VALIDEZ FISCAL</div>' : ''}
    ${isCopy ? '<div class="center">— Copia —</div>' : ''}
    <div class="sep"></div>
    <div>Cliente: ${esc(doc.receptorName || (doc.receptorCondition === 'CF' ? 'Consumidor final' : '—'))}</div>
    <div class="small">${esc(RECEPTOR_CONDITION_LABELS[doc.receptorCondition])}${doc.docTipo !== 99 ? ` · ${DOC_LABEL[doc.docTipo] ?? 'Doc.'} ${esc(doc.docTipo === 80 ? formatCuit(doc.docNro) : doc.docNro)}` : ''}</div>
    <div class="sep"></div>
    ${items}
    <div class="sep"></div>
    ${vat}
    <div class="row total"><span>TOTAL</span><span>${m2(doc.totalCents)}</span></div>
    ${transparency}
    <div class="sep"></div>
    <div class="row"><span>CAE</span><span>${esc(doc.cae ?? '')}</span></div>
    <div class="row"><span>Vto. CAE</span><span>${esc(doc.caeDueDate ? ymd(doc.caeDueDate) : '')}</span></div>
    ${doc.qrDataUrl ? `<div class="center"><img class="qr" src="${doc.qrDataUrl}" alt="QR ARCA"></div>` : ''}
    <footer>
      ${lines(cfg.footerText)}
      <div class="legal">Comprobante autorizado por ARCA</div>
    </footer>`;
}

/** HTML del ticket térmico. Funciona con cualquier impresora de 58 u 80 mm instalada en la computadora. */
export function ticketHtml(sale: SaleDTO, cfg: TicketConfig = sale.print.ticket, isCopy = false): string {
  const p = sale.print;
  const body = `
    <header>
      <div class="biz">${esc(p.businessName)}</div>
      ${p.cuit ? `<div>CUIT ${esc(p.cuit)}</div>` : ''}
      ${p.branchAddress ? `<div>${esc(p.branchAddress)}</div>` : ''}
      ${lines(cfg.headerText)}
    </header>
    <div class="sep"></div>
    <div class="row"><span>Ticket ${esc(sale.ticket)}</span><span>${esc(when.format(new Date(sale.createdAt)))}</span></div>
    ${cfg.showCashier ? `<div>Atendió: ${esc(sale.cashierName)}</div>` : ''}
    ${sale.status === 'VOIDED' ? '<div class="void">*** VENTA ANULADA ***</div>' : ''}
    ${isCopy ? '<div class="center">— Copia —</div>' : ''}
    <div class="sep"></div>
    ${sale.lines
      .map(
        (l) => `
      <div class="item">${esc(l.productName)}</div>
      <div class="row small"><span>${
        l.unitType === 'WEIGHT' ? `${kilos.format(l.qty / 1000)} kg x ${m(l.unitPriceCents)}/kg` : `${l.qty} u. x ${m(l.unitPriceCents)}`
      }</span><span>${m(l.lineTotalCents)}</span></div>`,
      )
      .join('')}
    <div class="sep"></div>
    ${sale.discountCents > 0 ? `<div class="row"><span>Subtotal</span><span>${m(sale.subtotalCents)}</span></div><div class="row"><span>Descuento</span><span>-${m(sale.discountCents)}</span></div>` : ''}
    <div class="row total"><span>TOTAL</span><span>${m(sale.totalCents)}</span></div>
    ${sale.payments.map((pay) => `<div class="row small"><span>${payLabel(pay)}</span><span>${m(pay.amountCents)}</span></div>`).join('')}
    ${sale.cashTenderedCents ? `<div class="row small"><span>Pagó con</span><span>${m(sale.cashTenderedCents)}</span></div>` : ''}
    ${sale.changeCents > 0 ? `<div class="row"><span>Vuelto</span><span>${m(sale.changeCents)}</span></div>` : ''}
    ${
      sale.customer
        ? `<div class="sep"></div><div>Cliente: <strong>${esc(sale.customer.name)}</strong></div>${
            sale.customer.balanceAfterCents !== null
              ? `<div class="row"><span>Saldo en cuenta corriente</span><span>${m(sale.customer.balanceAfterCents)}</span></div><div class="small" style="margin-top:8px">Firma: ____________________</div>`
              : ''
          }`
        : ''
    }
    <div class="sep"></div>
    <footer>
      ${lines(cfg.footerText)}
      <div class="legal">Comprobante no válido como factura</div>
    </footer>`;
  return body;
}

function documentHtml(pages: string[], cfg: TicketConfig): string {
  // Área imprimible típica: 72 mm en papel de 80 y 48 mm en papel de 58.
  const width = cfg.paperWidth === 58 ? 48 : 72;
  const size = cfg.largeText ? (cfg.paperWidth === 58 ? 12 : 14) : cfg.paperWidth === 58 ? 10 : 12;
  return `<!doctype html><html><head><meta charset="utf-8"><title>Ticket</title><style>
    @page { size: ${cfg.paperWidth}mm auto; margin: 0; }
    * { box-sizing: border-box; }
    body { margin: 0; padding: 3mm 0; width: ${width}mm; margin-left: auto; margin-right: auto;
      font-family: "Arial Narrow", Arial, Helvetica, sans-serif; font-size: ${size}px; line-height: 1.35; color: #000; }
    header, footer, .center { text-align: center; }
    .biz { font-size: 1.35em; font-weight: 700; margin-bottom: 2px; }
    .sep { border-top: 1px dashed #000; margin: 5px 0; }
    .row { display: flex; justify-content: space-between; gap: 6px; }
    .row span:last-child { text-align: right; white-space: nowrap; }
    .item { font-weight: 700; margin-top: 3px; }
    .small { font-size: 0.92em; }
    .total { font-size: 1.4em; font-weight: 700; margin: 3px 0; }
    .void { text-align: center; font-weight: 700; margin: 4px 0; }
    .legal { margin-top: 4px; font-size: 0.85em; }
    .page + .page { page-break-before: always; }
    .fiscal-head { display: flex; align-items: center; gap: 8px; margin: 2px 0; }
    .letter { border: 2px solid #000; font-size: 1.9em; font-weight: 700; width: 1.6em; height: 1.6em; display: inline-flex; align-items: center; justify-content: center; }
    .qr { width: 32mm; height: 32mm; margin-top: 4px; }
  </style></head><body>${pages.map((p) => `<div class="page">${p}</div>`).join('')}</body></html>`;
}

/**
 * Imprime el ticket en la impresora predeterminada de la computadora.
 * Para que no aparezca el cuadro de impresión, en Chrome se puede usar el modo kiosco
 * (lo explica la pantalla de Configuración).
 */
export function printSale(sale: SaleDTO, cfg: TicketConfig = sale.print.ticket, fiscal: FiscalDocumentDTO | null = saleInvoice(sale)): void {
  const pages = Array.from({ length: cfg.copies }, (_, i) => (fiscal ? fiscalHtml(sale, fiscal, cfg, i > 0) : ticketHtml(sale, cfg, i > 0)));
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  if (!doc || !frame.contentWindow) {
    frame.remove();
    return;
  }
  doc.open();
  doc.write(documentHtml(pages, cfg));
  doc.close();
  const win = frame.contentWindow;
  const cleanup = () => setTimeout(() => frame.remove(), 1000);
  win.addEventListener('afterprint', cleanup, { once: true });
  setTimeout(() => {
    win.focus();
    win.print();
    // Algunos navegadores no avisan "afterprint": se limpia igual.
    setTimeout(cleanup, 60_000);
  }, 50);
}

/** Venta de ejemplo para la vista previa y la impresión de prueba en Configuración. */
export function sampleSale(businessName: string, cfg: TicketConfig, branchAddress: string | null): SaleDTO {
  return {
    id: 'muestra',
    ticket: '0001-00000123',
    offline: false,
    branchId: '',
    branchName: '',
    sessionId: '',
    cashierName: 'Cajero de prueba',
    status: 'COMPLETED',
    subtotalCents: 3_175_000,
    discountCents: 0,
    totalCents: 3_175_000,
    costCents: null,
    cashTenderedCents: 3_500_000,
    changeCents: 325_000,
    notes: null,
    createdAt: new Date().toISOString(),
    voidedAt: null,
    voidedByName: null,
    voidReason: null,
    lines: [
      {
        id: '1',
        productId: '',
        productName: 'Asado de tira',
        unitType: 'WEIGHT',
        qty: 1250,
        unitPriceCents: 1_500_000,
        lineTotalCents: 1_875_000,
        barcode: null,
      },
      {
        id: '2',
        productId: '',
        productName: 'Carbón 4 kg',
        unitType: 'UNIT',
        qty: 2,
        unitPriceCents: 650_000,
        lineTotalCents: 1_300_000,
        barcode: null,
      },
    ],
    payments: [{ method: 'CASH', amountCents: 3_175_000, reference: null, installments: null, terminal: null }],
    customer: null,
    canVoid: false,
    print: {
      businessName: cfg.businessName || businessName,
      cuit: null,
      branchAddress,
      ticket: cfg,
    },
    fiscal: [],
    mpRefunds: [],
  };
}

/** Resumen de cierre de caja para imprimir y guardar con el efectivo. */
export function printCashSummary(s: import('@bifeai/shared').CashSessionDTO, businessName: string, cfg: TicketConfig): void {
  const row = (a: string, b: string, strong = false) =>
    `<div class="row${strong ? ' total' : ''}"><span>${esc(a)}</span><span>${esc(b)}</span></div>`;
  const methods = Object.entries(s.byMethod)
    .map(([k, v]) => row(PAYMENT_METHOD_LABELS[k as keyof typeof PAYMENT_METHOD_LABELS], m(v ?? 0)))
    .join('');
  const html = `
    <header><div class="biz">${esc(businessName)}</div><div>${esc(s.branchName)}${s.deviceName ? ` · ${esc(s.deviceName)}` : ''}</div>
    <div><strong>${s.status === 'CLOSED' ? 'CIERRE DE CAJA' : 'ESTADO DE CAJA'}</strong></div></header>
    <div class="sep"></div>
    ${row('Apertura', when.format(new Date(s.openedAt)))}
    ${row('Abrió', s.openedByName ?? '—')}
    ${s.closedAt ? row('Cierre', when.format(new Date(s.closedAt))) : ''}
    ${s.closedByName ? row('Cerró', s.closedByName) : ''}
    <div class="sep"></div>
    ${row('Ventas', `${s.salesCount} · ${m(s.salesCents)}`)}
    ${methods}
    <div class="sep"></div>
    ${s.collectionsCents ? row('Cobros de fiado', m(s.collectionsCents)) : ''}
    ${s.collectionsByMethod.CASH ? row('  en efectivo', m(s.collectionsByMethod.CASH)) : ''}
    <div class="sep"></div>
    ${row('Fondo inicial', m(s.openingCents))}
    ${row('Ingresos', m(s.depositsCents))}
    ${row('Retiros', m(s.withdrawalsCents))}
    ${row('Efectivo esperado', m(s.expectedCents), true)}
    ${s.countedCents !== null ? row('Efectivo contado', m(s.countedCents)) : ''}
    ${s.differenceCents !== null ? row(s.differenceCents < 0 ? 'Faltante' : s.differenceCents > 0 ? 'Sobrante' : 'Diferencia', m(Math.abs(s.differenceCents)), true) : ''}
    ${s.closeNotes ? `<div class="sep"></div><div>${esc(s.closeNotes)}</div>` : ''}
    <div class="sep"></div><footer><div>Firma: ____________________</div></footer>`;
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(frame);
  const doc = frame.contentDocument!;
  doc.open();
  doc.write(documentHtml([html], { ...cfg, copies: 1 }));
  doc.close();
  setTimeout(() => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    setTimeout(() => frame.remove(), 60_000);
  }, 50);
}

/** Imprime una o varias "hojas" de ticket en la impresora predeterminada. */
function printPages(pages: string[], cfg: TicketConfig): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  if (!doc || !frame.contentWindow) {
    frame.remove();
    return;
  }
  doc.open();
  doc.write(documentHtml(pages, cfg));
  doc.close();
  const win = frame.contentWindow;
  // Al terminar se saca la hoja y el foco vuelve al mostrador (si no, el lector "escribe" en la hoja oculta).
  const cleanup = () =>
    setTimeout(() => {
      frame.remove();
      window.focus();
    }, 500);
  win.addEventListener('afterprint', cleanup, { once: true });
  setTimeout(() => {
    win.focus();
    win.print();
    setTimeout(cleanup, 60_000);
  }, 50);
}

const head = (businessName: string, title: string) =>
  `<header><div class="biz">${esc(businessName)}</div><div><strong>${esc(title)}</strong></div><div>${esc(when.format(new Date()))}</div></header><div class="sep"></div>`;
const row = (a: string, b: string, cls = '') => `<div class="row ${cls}"><span>${esc(a)}</span><span>${esc(b)}</span></div>`;

/** Comprobante de pago de cuenta corriente (una copia para el cliente y otra si se configuró). */
export function printPaymentReceipt(customer: CustomerDTO, movement: CustomerMovementDTO, businessName: string, cfg: TicketConfig): void {
  const page = (copy: boolean) => `
    ${head(businessName, 'RECIBO DE PAGO')}
    ${copy ? '<div class="center">— Copia —</div>' : ''}
    <div>Cliente: <strong>${esc(customer.name)}</strong></div>
    ${customer.docNumber ? `<div>DNI/CUIT: ${esc(customer.docNumber)}</div>` : ''}
    <div class="sep"></div>
    ${row('Pagó', m(-movement.amountCents), 'total')}
    ${movement.method ? row('Medio', PAYMENT_METHOD_LABELS[movement.method], 'small') : ''}
    ${movement.reference ? row('Referencia', movement.reference, 'small') : ''}
    ${row('Fecha', when.format(new Date(movement.createdAt)), 'small')}
    ${movement.createdByName ? row('Recibió', movement.createdByName, 'small') : ''}
    <div class="sep"></div>
    ${row('Saldo pendiente', m(movement.balanceAfterCents), 'total')}
    <div class="sep"></div><footer>${lines(cfg.footerText)}<div class="legal">Comprobante no válido como factura</div></footer>`;
  printPages(
    Array.from({ length: cfg.copies }, (_, i) => page(i > 0)),
    cfg,
  );
}

/** Resumen de cuenta corriente: últimos movimientos con el saldo. */
export function printStatement(customer: CustomerDTO, businessName: string, cfg: TicketConfig, limit = 30): void {
  const moves = customer.movements.slice(0, limit).reverse();
  const html = `
    ${head(businessName, 'CUENTA CORRIENTE')}
    <div>Cliente: <strong>${esc(customer.name)}</strong></div>
    ${customer.phone ? `<div>Tel.: ${esc(customer.phone)}</div>` : ''}
    <div class="sep"></div>
    ${moves
      .map(
        (
          mv,
        ) => `<div class="small">${esc(new Date(mv.createdAt).toLocaleDateString('es-AR'))} · ${esc(CUSTOMER_MOVEMENT_LABELS[mv.type])}${mv.ticket ? ` ${esc(mv.ticket)}` : ''}${mv.reversed ? ' (anulado)' : ''}</div>
        <div class="row small"><span>${mv.amountCents > 0 ? '+' : '−'}${m(Math.abs(mv.amountCents))}</span><span>Saldo ${m(mv.balanceAfterCents)}</span></div>`,
      )
      .join('')}
    <div class="sep"></div>
    ${row('SALDO', m(customer.balanceCents), 'total')}
    <div class="sep"></div><footer>${lines(cfg.footerText)}</footer>`;
  printPages([html], { ...cfg, copies: 1 });
}

/**
 * Comprobante de "cajón abierto sin venta". Al imprimirlo, la impresora abre el cajón
 * (si en su driver está activado "abrir cajón al imprimir").
 */
export function printDrawerSlip(businessName: string, cashierName: string, reason: string, cfg: TicketConfig): void {
  const html = `
    ${head(businessName, 'APERTURA DE CAJÓN')}
    <div class="center">Sin venta</div>
    <div class="sep"></div>
    ${row('Cajero', cashierName)}
    <div>Motivo: ${esc(reason)}</div>`;
  printPages([html], { ...cfg, copies: 1 });
}
